// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * An alarm's domain, classified exactly as the Asset Directory classifies the
 * alarm's asset.
 *
 * An alarm's `assetId` (AEM `equipment_id`) is the digital twin entity id, so
 * the alarm's asset IS a directory item, and its domain is that item's domain
 * (utils/assetDomain). The rule-id prefix and AEM's free-text equipment_type
 * are the fallbacks for an asset the twin doesn't hold.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { twinQueryOptions } from './useDigitalTwin';
import { useAuth } from './useAuth';
import { alarmDomain, type AssetDomain } from '../utils/assetDomain';
import type { Alarm } from '../types/alarm';

export interface AssetDomainOf {
  domainOf: (alarm: Pick<Alarm, 'assetId' | 'group' | 'ruleId' | 'id'>) => AssetDomain;
  /**
   * True while the twin is still loading, so a domain may still change once
   * it lands. Callers hold off on anything that would act on a provisional
   * domain, like clearing a `?group=` that only the twin can supply. False
   * with no site, and false once the read fails (the fallbacks are final then).
   */
  pending: boolean;
}

export function useAssetDomainOf(siteId: string | undefined): AssetDomainOf {
  const { sites } = useAuth();
  // Same guard as useDigitalTwin: only a site the user has loaded is fetched.
  const twinSiteId = siteId && sites.some(s => s.site_id === siteId) ? siteId : undefined;

  // Types only need the structural graph — the cheaper payload, without the
  // telemetry the live graph carries. Same cache entry the twin pages use.
  // ponytail: one site's twin; the fleet-wide list (no site) classifies by
  // rule prefix and equipment_type. Load each site's twin there if needed.
  const { data, isPending } = useQuery({
    ...twinQueryOptions(twinSiteId ?? '', ''),
    enabled: !!twinSiteId,
  });

  const typeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const entity of data?.entities ?? []) map.set(entity.id, entity.type);
    return map;
  }, [data]);

  const domainOf = useCallback<AssetDomainOf['domainOf']>(
    alarm => alarmDomain(alarm, alarm.assetId ? typeById.get(alarm.assetId) : undefined),
    [typeById]
  );
  return { domainOf, pending: !!twinSiteId && isPending };
}
